<!-- src/components/ContactCTA.vue -->
<template>
  <div class="contact-cta">
    <p class="intro-text text-center">
      For a more formal proposal, send a message using this form or the contact
      details below.
    </p>

    <form @submit.prevent="handleSubmit">
      <input
        type="text"
        placeholder="Your Name"
        v-model="formData.name"
        required
        class="input-top"
      />
      <input
        type="email"
        placeholder="Your Email"
        v-model="formData.email"
        required
        class="input-middle"
      />
      <textarea
        placeholder="Your Message"
        v-model="formData.message"
        required
        class="textarea-bottom"
      ></textarea>
      <div class="sendMessage-section">
        <button type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? "Sending..." : buttonText }}
        </button>
      </div>
      <div
        v-if="requestMessage"
        :class="requestSuccess ? 'success-message' : 'error-message'"
      >
        {{ requestMessage }}
      </div>
    </form>

    <div class="contact-info">
      <div class="contact-details">
        <p class="text-center">
          Email Patricia at <strong>the33path@the33path</strong> <br />
          or call
          <strong>+351 919 574 161</strong> for a formal proposal
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref, computed } from "vue";

export default {
  name: "ContactCTA",
  props: {
    contactInfo: {
      type: Object,
      required: true,
    },
    origin: {
      type: String,
      default: "contact",
      validator: (value) => {
        return [
          "contact",
          "requestAccess",
          "notifyContent",
          "accessContent",
        ].includes(value);
      },
    },
  },
  setup(props) {
    const formData = reactive({
      name: "",
      email: "",
      message: "",
    });

    const requestMessage = ref("");
    const requestSuccess = ref(false);
    const isSubmitting = ref(false);

    // Button text based on the origin of the request
    const buttonText = computed(() => {
      switch (props.origin) {
        case "requestAccess":
          return "Request Website Access";
        case "notifyContent":
          return "Notify Me When Available";
        case "accessContent":
          return "Request Content Access";
        default:
          return "Send Message";
      }
    });

    // Email subject based on the origin of the request
    const emailSubject = computed(() => {
      switch (props.origin) {
        case "requestAccess":
          return "Website Access Request";
        case "notifyContent":
          return "Content Availability Notification Request";
        case "accessContent":
          return "Content Access Request";
        default:
          return "Contact Message";
      }
    });

    const handleSubmit = async () => {
      // Validate form
      if (!formData.name || !formData.email || !formData.message) {
        requestMessage.value = "Please fill in all fields.";
        requestSuccess.value = false;
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        requestMessage.value = "Please enter a valid email address.";
        requestSuccess.value = false;
        return;
      }

      requestMessage.value = "";
      requestSuccess.value = false;
      isSubmitting.value = true;

      try {
        const response = await fetch(
          "https://us-central1-the33path.cloudfunctions.net/sendEmailOnRequest",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              message: formData.message,
              subject: emailSubject.value, // Add the dynamic subject
              origin: props.origin, // Add the origin for further processing if needed
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        requestSuccess.value = true;
        requestMessage.value =
          "Your message has been sent successfully. We'll contact you soon.";

        // Reset form after 3 seconds
        setTimeout(() => {
          formData.name = "";
          formData.email = "";
          formData.message = "";
          requestMessage.value = "";
        }, 3000);
      } catch (error) {
        requestSuccess.value = false;
        requestMessage.value =
          "There was an error sending your message. Please try again later.";
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      contactInfo: props.contactInfo,
      formData,
      handleSubmit,
      requestMessage,
      requestSuccess,
      isSubmitting,
      buttonText,
      emailSubject,
    };
  },
};
</script>

<style scoped>
.contact-cta {
  width: 100%;
  overflow: hidden;
  position: relative;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--doubleSpace) var(--space);
}

.intro-text {
  margin-bottom: var(--doubleSpace);
  max-width: 700px;
  font-size: var(--regularFontSize);
}

.contact-cta h1 {
  margin-bottom: 20px;
}

.contact-cta form {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 0;
  margin-bottom: var(--doubleSpace);
}

.contact-cta input,
.contact-cta textarea {
  width: 100%;
  max-width: 500px;
  margin-bottom: 10px;
  padding: 10px;
  font-size: 1rem;
}

.sendMessage-section {
  margin-top: var(--largeSpace);
  display: flex;
  justify-content: center;
  width: 100%;
}

.contact-info {
  margin-top: var(--doubleSpace);
  width: 100%;
  max-width: 600px;
}

.success-message {
  color: var(--green);
  margin-top: var(--space);
}

.error-message {
  color: var(--red);
  margin-top: var(--space);
}

/* Media queries for mobile devices */
@media (max-width: 480px) {
  .contact-cta {
    padding: var(--space);
  }

  .intro-text {
    margin-bottom: var(--largeSpace);
  }

  .contact-info {
    margin-top: var(--largeSpace);
  }
}
</style>
